<template>
  <div v-if="show" class="quicksearch-overlay" @click.self="close">
    <div class="quicksearch-modal" role="dialog" aria-modal="true">
      <!-- Logo -->
      <div class="d-flex justify-content-center mb-4">
        <img :src="logoPolgo" alt="Logo Polgo branco" style="height: 56px;" />
      </div>
      <!-- Campo de busca -->
      <div class="quicksearch-searchbar-row mb-4">
        <div class="quicksearch-searchbar-group">
          <span class="quicksearch-searchbar-icon"><i class="fas fa-search"></i></span>
          <input v-model="search" @input="onInput" @keydown="onKeydown" ref="searchInput" type="text"
            class="quicksearch-searchbar-input" :placeholder="'Digite o nome do cliente, aplicação ou atalho...'"
            aria-label="Buscar cliente, aplicação ou atalho" autocomplete="off" />
          <div class="input-group-append" v-if="loading">
            <span class="input-group-text bg-white border-left-0">
              <i class="fas fa-spinner fa-spin"></i>
            </span>
          </div>
        </div>
      </div>
      <template v-if="search.trim()">
        <div class="quicksearch-grid-container">
          <div
            v-if="filteredOAuthApps.length"
            class="quicksearch-apps-panel"
          >
            <div class="quicksearch-section-title">Apps e Atalhos</div>
            <div class="quicksearch-apps-grid">
              <a
                v-for="app in filteredOAuthApps"
                :key="app.id"
                class="quicksearch-app-tile"
                :href="app.href"
                target="_blank"
                rel="noopener noreferrer"
              >
                <app-icon
                  :icon="app.icon"
                  :gradient="app.gradient"
                  size="md"
                />
                <span class="quicksearch-app-tile__label" v-html="highlight(app.label)" />
              </a>
            </div>
          </div>

          <div v-if="filteredClientes.length" class="quicksearch-grid">
            <div v-for="cliente in filteredClientes" :key="cliente.identificacao" class="cliente-grid-item" tabindex="0"
              role="region" :aria-label="'Cliente ' + cliente.nome">
              <!-- Cabeçalho do cliente -->
              <div class="cliente-header">
                <ClienteAvatar :nome="cliente.nome" :identificacao="cliente.identificacao" size="medium" />
                <div class="cliente-info">
                  <div class="cliente-nome" v-html="highlight(cliente.nome)"></div>
                  <div class="cliente-identificacao" v-html="highlight(cliente.identificacao)"></div>
                </div>
                <!-- Badge de status inativo -->
                <div v-if="cliente.statusCliente === 'INATIVO'" class="status-badge inativo">
                  <i class="fas fa-pause-circle"></i>
                  INATIVO
                </div>
              </div>

              <!-- Atalhos organizados em grid interno -->
              <div class="atalhos-grid">
                <div class="atalho-item">
                  <AtalhoDropdownBtn label="Dashboard" icon="fas fa-tachometer-alt" :links="cliente.atalhos.dashboard"
                    color="primary" :cliente-id="cliente.identificacao" categoria="dashboard"
                    :disabled="cliente.statusCliente === 'INATIVO'" @abrir-link="abrirLink" />
                </div>
                <div class="atalho-item">
                  <AtalhoDropdownBtn label="PWA" icon="fas fa-mobile-alt" :links="cliente.atalhos.pwa" color="success"
                    :cliente-id="cliente.identificacao" categoria="pwa" 
                    :disabled="cliente.statusCliente === 'INATIVO' || !cliente.temPWAKRAKEN"
                    @abrir-link="abrirLink" @mostrar-qrcode="mostrarQRCode" @carregar-atalhos="onCarregarAtalhos" />
                </div>
                <div class="atalho-item">
                  <AtalhoSimpleBtn label="Landing Page" icon="fas fa-globe" color="info"
                    :cliente-id="cliente.identificacao" categoria="lp" 
                    @abrir-link="abrirLink" @mostrar-qrcode="mostrarQRCode" />
                </div>
                <div class="atalho-item">
                  <AtalhoDropdownBtn label="Configurações" icon="fas fa-cog" :links="cliente.atalhos.configuracoes"
                    color="warning" :cliente-id="cliente.identificacao" categoria="configuracoes"
                    @abrir-link="abrirLink" />
                </div>
              </div>
            </div>
          </div>

          <!-- Estados vazios -->
          <div v-if="!loading && filteredClientes.length === 0 && filteredOAuthApps.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-search"></i>
            </div>
            <div class="empty-text">Nenhum cliente, aplicação ou atalho encontrado</div>
            <div class="empty-subtext">Tente usar termos diferentes na busca</div>
          </div>

          <div v-if="loading && !filteredClientes.length && !filteredOAuthApps.length" class="loading-state">
            <div class="skeleton-item" v-for="i in 3" :key="i">
              <div class="skeleton-avatar"></div>
              <div class="skeleton-content">
                <div class="skeleton-line"></div>
                <div class="skeleton-line short"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="quicksearch-hint text-center mt-3">
          Pressione <b>Esc</b> para fechar • <b>Ctrl+P</b> para abrir
        </div>
      </template>
      <button class="quicksearch-close" @click="close" aria-label="Fechar pesquisa">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Modal de seleção de branch -->
    <BranchSelectorModal :show="showBranchModal" :app-name="selectedAppForBranch" :cliente="selectedClienteForBranch"
      @close="showBranchModal = false" @select="onBranchSelect" @mostrar-qrcode-branch="mostrarQRCodeBranch" />

    <!-- Modal de QR Code -->
    <QRCodeModal 
      :show="showQRCodeModal" 
      :url="qrCodeUrl" 
      :title="qrCodeTitle"
      @close="showQRCodeModal = false" 
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ShortcutBtn from './ShortcutBtn.vue';
import AtalhoDropdownBtn from './AtalhoDropdownBtn.vue';
import AtalhoSimpleBtn from './AtalhoSimpleBtn.vue';
import ClienteAvatar from '../ClienteAvatar.vue';
import AppIcon from '@/components/layout/AppIcon.vue';
import BranchSelectorModal from './BranchSelectorModal.vue';
import QRCodeModal from './QRCodeModal.vue';
import { mapOAuthAtalhosToShortcuts } from '@/lib/oauthAppFormUtils';

const tipoToLabel = {
  prod: 'PROD', test: 'TEST', branch: 'BRANCH', pwakraken: 'PWAKRAKEN', qrbeta: 'QRBETA', dashboard: 'DASHBOARD', servidor: 'SERVIDOR',
};

export default {
  name: 'ClienteQuickSearch',
  components: {
    ShortcutBtn,
    AtalhoDropdownBtn,
    AtalhoSimpleBtn,
    ClienteAvatar,
    AppIcon,
    BranchSelectorModal,
    QRCodeModal,
  },
  props: {
    show: { type: Boolean, default: false },
  },
  data() {
    return {
      search: '',
      searchTimeout: null,
      loadingBusca: false,
      logoPolgo: require('@/assets/images/logo_branco.png'),
      atalhosCarregados: {}, // Cache de atalhos por cliente
      pwaConfigCache: {}, // Cache de verificação de PWAKRAKEN por cliente
      showBranchModal: false,
      selectedClienteForBranch: null,
      selectedAppForBranch: 'DASHBOARD', // DASHBOARD ou PWAKRAKEN
      showQRCodeModal: false,
      qrCodeUrl: '',
      qrCodeTitle: 'Acesso Rápido',
      oauthApps: [],
      loadingOAuthApps: false,
    };
  },
  computed: {
    ...mapGetters({
      clientes: 'clientes/getCustomerList',
      loadingStore: 'clientes/getLoadingState',
    }),
    columns() {
      return [
        { label: 'Cliente', field: 'cliente', thClass: 'text-center', tdClass: 'vgt-polgo-vertical-align' },
        { label: 'Dashboard', field: 'dashboard', thClass: 'text-center', tdClass: 'vgt-polgo-vertical-align' },
        { label: 'PWA', field: 'pwa', thClass: 'text-center', tdClass: 'vgt-polgo-vertical-align' },
        { label: 'LP', field: 'lp', thClass: 'text-center', tdClass: 'vgt-polgo-vertical-align' },
        { label: 'Configurações', field: 'configuracoes', thClass: 'text-center', tdClass: 'vgt-polgo-vertical-align' },
      ];
    },
    clientesComAtalhos() {
      if (!this.clientes || this.clientes.length === 0) {
        return [];
      }
      return this.clientes.map(c => {
        const atalhos = this.atalhosCarregados[c.identificacao] || {
          dashboard: {},
          pwa: {},
          lp: {},
          configuracoes: {}
        };
        // Verificar se tem PWAKRAKEN: 
        // - Se está no cache e é false, não tem
        // - Se está no cache e é true, tem
        // - Se não está no cache (undefined), assumir true inicialmente (não desabilitar até verificar)
        const temPWAKRAKEN = this.pwaConfigCache[c.identificacao] !== false;
        return {
          ...c,
          atalhos: atalhos,
          temPWAKRAKEN: temPWAKRAKEN
        };
      });
    },
    filteredClientes() {
      const termo = this.search.trim().toLowerCase();
      if (!termo) return [];
      
      // Verificar se há clientes disponíveis
      if (!this.clientes || this.clientes.length === 0) {
        return [];
      }
      
      let lista = this.clientesComAtalhos;
      if (termo) {
        lista = lista.filter(c =>
          (c.nome && c.nome.toLowerCase().includes(termo)) ||
          (c.identificacao && c.identificacao.toLowerCase().includes(termo))
        );
      }

      // Ordenar: clientes ativos primeiro, depois inativos
      // Isso melhora a experiência do usuário priorizando clientes relevantes
      lista.sort((a, b) => {
        const aAtivo = a.statusCliente === 'ATIVO';
        const bAtivo = b.statusCliente === 'ATIVO';

        // Se um é ativo e outro não, ativo vem primeiro
        if (aAtivo && !bAtivo) return -1;
        if (!aAtivo && bAtivo) return 1;

        // Se ambos têm o mesmo status, manter ordem alfabética por nome
        return (a.nome || '').localeCompare(b.nome || '');
      });

      return lista.slice(0, 10);
    },
    filteredOAuthApps() {
      const termo = this.search.trim().toLowerCase();
      if (!termo || !this.oauthApps.length) {
        return [];
      }

      return this.oauthApps
        .filter((app) => app.label && app.label.toLowerCase().includes(termo))
        .slice(0, 10);
    },
    loading() {
      return this.loadingStore || this.loadingBusca || this.loadingOAuthApps;
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.carregarAppsOAuth();

        // Carregar clientes se a lista estiver vazia
        if (!this.clientes || this.clientes.length === 0) {
          this.fetchCustomers().then(() => {
            // Após carregar clientes, verificar PWAKRAKEN para os clientes filtrados
            this.verificarPWAKRAKENEmLote();
          });
        } else {
          // Se já tem clientes, verificar PWAKRAKEN para os que ainda não foram verificados
          this.verificarPWAKRAKENEmLote();
        }
        
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.select();
          }
        });
      }
    },
    filteredClientes: {
      handler() {
        // Quando a lista filtrada mudar, verificar PWAKRAKEN para os novos clientes
        this.verificarPWAKRAKENEmLote();
      },
      immediate: false
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleGlobalKey);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleGlobalKey);
  },
  methods: {
    ...mapActions({
      fetchCustomers: 'clientes/fetchCustomers',
      fetchSettingByClienteENome: 'configuracoes/fetchSettingByClienteENome',
      listarAtalhosOAuth: 'oauth/listarAtalhos',
    }),

    async carregarAppsOAuth() {
      this.loadingOAuthApps = true;
      try {
        const response = await this.listarAtalhosOAuth();
        if (response.status === 200) {
          this.oauthApps = mapOAuthAtalhosToShortcuts(response.dados?.atalhos || []);
        }
      } finally {
        this.loadingOAuthApps = false;
      }
    },

    async buscarAtalhosReais(identificacao) {
      console.log('🚨 buscarAtalhosReais iniciado para:', identificacao);
      // Verificar se já temos os atalhos em cache
      if (this.atalhosCarregados[identificacao]) {
        console.log('Atalhos já em cache para:', identificacao);
        return this.atalhosCarregados[identificacao];
      }

      try {
        // Organizar URLs por tipo
        const atalhos = {
          dashboard: {},
          pwa: {},
          lp: {},
          configuracoes: {}
        };

        // Dashboard, PWA e LP agora usam plugin SSO, não precisam de URLs
        // A lógica de busca da configuração PWAKRAKEN foi movida para o plugin SSO
        atalhos.dashboard = {
          prod: 'SSO_LOGIN',
          test: 'SSO_LOGIN',
          local: 'SSO_LOGIN'
        };

        atalhos.pwa = {
          prod: 'SSO_LOGIN',
          test: 'SSO_LOGIN',
          local: 'SSO_LOGIN'
        };

        atalhos.lp = {
          prod: 'SSO_LOGIN'
        };

        // Adicionar links de configuração
        atalhos.configuracoes = {
          pwakraken: `/configuracoes/editar/${identificacao}/PWAKRAKEN`,
          qrbeta: `/configuracoes/editar/${identificacao}/QRBETA`,
          dashboard: `/configuracoes/editar/${identificacao}/DASHBOARD`,
          servidor: `/configuracoes/editar/${identificacao}/SERVIDOR`,
        };

        // Salvar no cache
        this.atalhosCarregados[identificacao] = atalhos;
        console.log('Atalhos finais salvos no cache:', atalhos);
        return atalhos;
      } catch (error) {
        console.error('Erro ao buscar atalhos reais:', error);
        this.atalhosCarregados[identificacao] = {
          dashboard: {
            prod: 'SSO_LOGIN',
            test: 'SSO_LOGIN'
          },
          pwa: {
            prod: 'SSO_LOGIN',
            test: 'SSO_LOGIN'
          },
          lp: {
            prod: 'SSO_LOGIN'
          },
          configuracoes: {
            pwakraken: `/configuracoes/editar/${identificacao}/PWAKRAKEN`,
            qrbeta: `/configuracoes/editar/${identificacao}/QRBETA`,
            dashboard: `/configuracoes/editar/${identificacao}/DASHBOARD`,
            servidor: `/configuracoes/editar/${identificacao}/SERVIDOR`,
          }
        };
        return this.atalhosCarregados[identificacao];
      }
    },

    async carregarAtalhosCliente(clienteId) {
      // Se já temos os atalhos em cache, não precisa carregar novamente
      if (this.atalhosCarregados[clienteId]) {
        return this.atalhosCarregados[clienteId];
      }

      try {
        console.log('Chamando buscarAtalhosReais para:', clienteId);
        const atalhos = await this.buscarAtalhosReais(clienteId);
        console.log('Atalhos retornados do buscarAtalhosReais:', atalhos);
        
        // Verificar se tem configuração PWAKRAKEN (apenas se ainda não foi verificado)
        if (this.pwaConfigCache[clienteId] === undefined) {
          await this.verificarPWAKRAKEN(clienteId);
        }
        
        // Forçar reatividade
        this.$forceUpdate();
        return atalhos;
      } catch (error) {
        console.error('Erro ao carregar atalhos do cliente:', error);
        return null;
      }
    },
    
    async verificarPWAKRAKEN(clienteId) {
      try {
        const configPWAKRAKEN = await this.fetchSettingByClienteENome({
          cliente: clienteId,
          nome: 'PWAKRAKEN'
        });
        
        // Cache o resultado: true se encontrou, false se não encontrou
        this.pwaConfigCache[clienteId] = configPWAKRAKEN.status === 200 && configPWAKRAKEN.dados != null;
        
        // Forçar atualização da lista de clientes para refletir a mudança
        this.$forceUpdate();
      } catch (error) {
        console.error('Erro ao verificar PWAKRAKEN para cliente:', clienteId, error);
        // Se der erro, assumir que não tem (mais seguro)
        this.pwaConfigCache[clienteId] = false;
        this.$forceUpdate();
      }
    },
    
    async onCarregarAtalhos(clienteId) {
      // Carregar atalhos e verificar PWAKRAKEN
      await this.carregarAtalhosCliente(clienteId);
    },
    
    async verificarPWAKRAKENEmLote() {
      // Verificar PWAKRAKEN apenas para clientes que ainda não foram verificados
      const clientesParaVerificar = this.filteredClientes.filter(c => 
        this.pwaConfigCache[c.identificacao] === undefined
      );
      
      // Verificar em paralelo, mas limitar a 5 por vez para não sobrecarregar
      const batchSize = 5;
      for (let i = 0; i < clientesParaVerificar.length; i += batchSize) {
        const batch = clientesParaVerificar.slice(i, i + batchSize);
        await Promise.all(batch.map(c => this.verificarPWAKRAKEN(c.identificacao)));
      }
    },

    /**
     * Abre links ou executa ações para diferentes categorias
     * Para dashboard e PWA: usa plugin SSO para autenticação automática
     * Para outras categorias: abre URLs diretamente
     */
    async abrirLink({ clienteId, categoria, tipo }) {
      // Se for dashboard, usar o plugin SSO específico
      if (categoria === 'dashboard') {
        console.log('Usando plugin SSO para dashboard:', { clienteId, tipo });

        // Se o tipo for 'branch', abrir modal de seleção de branch
        if (tipo === 'branch') {
          this.selectedClienteForBranch = clienteId;
          this.selectedAppForBranch = 'DASHBOARD';
          this.showBranchModal = true;
          return;
        }

        // Verificar se o cliente está inativo
        const cliente = this.clientes.find(c => c.identificacao === clienteId);
        if (cliente && cliente.statusCliente === 'INATIVO') {
          console.log('Cliente inativo, dashboard indisponível:', clienteId);
          this.$swal({
            type: 'warning',
            title: 'Dashboard Indisponível',
            text: 'Este cliente está inativo. O dashboard não está disponível.',
            timer: 3000,
            showConfirmButton: false
          });
          return;
        }

        // Mapear tipo para ambiente
        const ambiente = tipo === 'prod' ? 'PRODUCAO' : tipo === 'local' ? 'LOCAL' : 'TESTE';

        try {
          // Usar o plugin SSO específico para dashboard
          await this.$sso.loginDashboard({
            cliente: clienteId,
            ambiente: ambiente,
            options: {
              showFeedback: true,
              onSuccess: (result) => {
                console.log('Login SSO Dashboard realizado com sucesso:', result);
              },
              onError: (error) => {
                console.error('Erro no login SSO Dashboard:', error);
              }
            }
          });
          return;
        } catch (error) {
          console.error('Erro ao usar plugin SSO Dashboard:', error);
          return;
        }
      }

      // Se for PWA, usar o plugin SSO específico
      if (categoria === 'pwa') {
        console.log('Usando plugin SSO para PWA:', { clienteId, tipo });

        // Verificar se tem configuração PWAKRAKEN antes de continuar
        if (this.pwaConfigCache[clienteId] === undefined) {
          await this.verificarPWAKRAKEN(clienteId);
        }
        
        if (!this.pwaConfigCache[clienteId]) {
          this.$swal({
            type: 'warning',
            title: 'PWA Indisponível',
            text: 'Este cliente não possui configuração PWAKRAKEN. O PWA não está disponível.',
            timer: 3000,
            showConfirmButton: false
          });
          return;
        }

        // Se o tipo for 'branch', abrir modal de seleção de branch
        if (tipo === 'branch') {
          this.selectedClienteForBranch = clienteId;
          this.selectedAppForBranch = 'PWAKRAKEN';
          this.showBranchModal = true;
          return;
        }

        // Mapear tipo para ambiente
        const ambiente = tipo === 'prod' ? 'PRODUCAO' : tipo === 'local' ? 'LOCAL' : 'TESTE';

        try {
          // Usar o plugin SSO específico para PWA
          await this.$sso.loginPWA({
            cliente: clienteId,
            ambiente: ambiente,
            options: {
              showFeedback: true,
              onSuccess: (result) => {
                console.log('Login SSO PWA realizado com sucesso:', result);
              },
              onError: (error) => {
                console.error('Erro no login SSO PWA:', error);
              }
            }
          });
          return;
        } catch (error) {
          console.error('Erro ao usar plugin SSO PWA:', error);
          return;
        }
      }

      // Se for LP, usar o plugin SSO específico
      if (categoria === 'lp') {
        console.log('Usando plugin SSO para LP:', { clienteId });

        try {
          // Usar o plugin SSO específico para LP
          await this.$sso.loginLP({
            cliente: clienteId,
            options: {
              showFeedback: true,
              onSuccess: (result) => {
                console.log('Login SSO LP realizado com sucesso:', result);
              },
              onError: (error) => {
                console.error('Erro no login SSO LP:', error);
              }
            }
          });
          return;
        } catch (error) {
          console.error('Erro ao usar plugin SSO LP:', error);
          return;
        }
      }

      // Para outras categorias, usar o fluxo normal
      let atalhos = this.atalhosCarregados[clienteId];
      console.log('Atalhos do cache:', atalhos);
      if (!atalhos) {
        console.log('Atalhos não encontrados no cache, carregando...');
        atalhos = await this.carregarAtalhosCliente(clienteId);
      }

      if (!atalhos) {
        console.error('Não foi possível carregar os atalhos');
        return;
      }

      // Tratar caso especial "todos" para configurações
      if (categoria === 'configuracoes' && tipo === 'todos') {
        const url = `/configuracoes?cliente=${clienteId}`;
        console.log('Abrindo página de todas as configurações:', url);
        window.open(url, '_blank');
        return;
      }

      // Usar a categoria específica
      const categoriaAtalhos = atalhos[categoria];
      if (!categoriaAtalhos) {
        console.error('Categoria de atalhos não encontrada:', categoria);
        return;
      }

      const url = categoriaAtalhos[tipo];
      console.log('Tentando abrir URL:', { categoria, tipo, url, categoriaAtalhos });
      if (url) {
        window.open(url, '_blank');
      } else {
        console.error('URL não encontrada para categoria:', categoria, 'tipo:', tipo);
      }
    },

    onInput() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.loadingBusca = true;
      this.searchTimeout = setTimeout(() => {
        this.loadingBusca = false;
      }, 200);
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.close();
      if ((e.ctrlKey && e.key === 'k') || e.key === '/') {
        e.preventDefault();
        this.$refs.searchInput && this.$refs.searchInput.focus();
      }
    },
    highlight(text) {
      const termo = this.search.trim();
      if (!termo) return text;
      const regex = new RegExp(`(${termo})`, 'gi');
      return text.replace(regex, '<mark>$1</mark>');
    },
    async onBranchSelect(branch) {
      console.log('Branch selecionada:', branch);
      console.log('App selecionado:', this.selectedAppForBranch);

      const cliente = this.selectedClienteForBranch;

      try {
        // Se for PWA (PWAKRAKEN), apenas redirecionar com configID
        if (this.selectedAppForBranch === 'PWAKRAKEN') {
          console.log('Abrindo PWA branch para cliente:', cliente);

          // Buscar configuração PWAKRAKEN para obter o ID
          const configPWAKRAKEN = await this.$store.dispatch('configuracoes/fetchSettingByClienteENome', {
            cliente: cliente,
            nome: 'PWAKRAKEN'
          });

          if (configPWAKRAKEN.status === 200 && configPWAKRAKEN.dados) {
            // Tentar pegar o ID de diferentes possíveis campos
            const configId = configPWAKRAKEN.dados._id || configPWAKRAKEN.dados.id || configPWAKRAKEN.id;

            if (!configId) {
              throw new Error('ID da configuração não encontrado');
            }

            console.log('Config ID encontrado:', configId);

            // Construir URL da branch com configID e ambiente
            const branchUrl = `${branch.url}/?configID=${configId}&ambiente=dev`;
            console.log('Abrindo PWA branch URL:', branchUrl);

            window.open(branchUrl, '_blank');

            // Fechar o modal
            this.showBranchModal = false;
          } else {
            throw new Error('Configuração PWAKRAKEN não encontrada');
          }

          return;
        }

        // Se for Dashboard, gerar token SSO de teste (homologação)
        const response = await this.$store.dispatch('autenticacao/initPolgoApiLoginSSOSession', {
          ambiente: 'TESTE',
          cliente
        });

        console.log('Resposta do SSO:', response);

        if ((response.status === 200 || response.status === 201) && response.tokenJWT) {
          console.log('Token SSO gerado com sucesso');

          // Abrir a URL da branch com o token
          const branchUrl = `${branch.url}/login?cliente=${cliente}&token=${response.tokenJWT}`;
          console.log('Abrindo Dashboard branch URL:', branchUrl);

          window.open(branchUrl, '_blank');

          // Fechar o modal
          this.showBranchModal = false;
        } else {
          throw new Error('Falha ao gerar token SSO');
        }
      } catch (error) {
        console.error('Erro ao processar seleção de branch:', error);
        this.$swal({
          type: 'error',
          title: 'Erro',
          text: error.message || 'Não foi possível abrir a branch selecionada.',
          timer: 3000,
          showConfirmButton: false
        });
      }
    },
    async mostrarQRCodeBranch(branch) {
      try {
        const cliente = this.selectedClienteForBranch;
        
        // Buscar nome do cliente
        const clienteObj = this.clientes.find(c => c.identificacao === cliente);
        const nomeCliente = clienteObj ? clienteObj.nome : cliente;

        let url = '';
        let title = '';

        // Se for PWA (PWAKRAKEN), construir URL com configID
        if (this.selectedAppForBranch === 'PWAKRAKEN') {
          // Verificar cache primeiro
          if (this.pwaConfigCache[cliente] === undefined) {
            await this.verificarPWAKRAKEN(cliente);
          }
          
          // Se não tem PWAKRAKEN, não permitir gerar QR Code
          if (!this.pwaConfigCache[cliente]) {
            this.$swal({
              type: 'warning',
              title: 'PWA Indisponível',
              text: 'Este cliente não possui configuração PWAKRAKEN. O PWA não está disponível.',
              timer: 3000,
              showConfirmButton: false
            });
            return;
          }
          
          // Buscar configuração PWAKRAKEN para obter o ID
          const configPWAKRAKEN = await this.$store.dispatch('configuracoes/fetchSettingByClienteENome', {
            cliente: cliente,
            nome: 'PWAKRAKEN'
          });

          if (configPWAKRAKEN.status === 200 && configPWAKRAKEN.dados) {
            const configId = configPWAKRAKEN.dados._id || configPWAKRAKEN.dados.id || configPWAKRAKEN.id;
            url = `${branch.url}/?configID=${configId}&ambiente=dev`;
            title = `${nomeCliente} - PWA Branch ${branch.name}`;
          } else {
            throw new Error('Configuração PWAKRAKEN não encontrada');
          }
        } 
        // Se for Dashboard, gerar token SSO e construir URL
        else if (this.selectedAppForBranch === 'DASHBOARD') {
          const response = await this.$store.dispatch('autenticacao/initPolgoApiLoginSSOSession', {
            ambiente: 'TESTE',
            cliente
          });

          if ((response.status === 200 || response.status === 201) && response.tokenJWT) {
            url = `${branch.url}/login?cliente=${cliente}&token=${response.tokenJWT}`;
            title = `${nomeCliente} - Dashboard Branch ${branch.name}`;
          } else {
            throw new Error('Falha ao gerar token SSO');
          }
        }

        if (url) {
          this.qrCodeUrl = url;
          this.qrCodeTitle = title;
          this.showQRCodeModal = true;
        } else {
          throw new Error('Não foi possível obter a URL da branch');
        }
      } catch (error) {
        console.error('Erro ao obter URL da branch para QR Code:', error);
        this.$swal({
          type: 'error',
          title: 'Erro',
          text: error.message || 'Não foi possível gerar o QR Code da branch.',
          timer: 3000,
          showConfirmButton: false
        });
      }
    },
    close() {
      this.$emit('close');
      this.search = '';
    },
    handleGlobalKey(e) {
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        if (this.show) {
          this.$nextTick(() => {
            if (this.$refs.searchInput) {
              this.$refs.searchInput.focus();
            }
          });
        } else {
          this.$emit('open');
        }
      }
      if (e.key === 'Escape' && this.show) {
        this.close();
      }
    },
    emitAtalho(cliente, url) {
      this.$emit('atalho', { cliente, url });
    },
    async mostrarQRCode({ clienteId, categoria, tipo }) {
      try {
        // Buscar nome do cliente
        const cliente = this.clientes.find(c => c.identificacao === clienteId);
        const nomeCliente = cliente ? cliente.nome : clienteId;

        let url = '';
        let title = '';

        // Para configurações, usar URL direta (sem modal de carregamento)
        if (categoria === 'configuracoes') {
          const atalhos = this.atalhosCarregados[clienteId] || await this.carregarAtalhosCliente(clienteId);
          if (atalhos && atalhos.configuracoes && atalhos.configuracoes[tipo]) {
            url = window.location.origin + atalhos.configuracoes[tipo];
            title = `${nomeCliente} - Configuração ${tipo.toUpperCase()}`;
            
            this.qrCodeUrl = url;
            this.qrCodeTitle = title;
            this.showQRCodeModal = true;
            return;
          } else {
            throw new Error('URL não encontrada');
          }
        } 
        // Para dashboard, PWA e LP, usar modal de carregamento do SSO
        else if (categoria === 'dashboard' || categoria === 'pwa' || categoria === 'lp') {
          if (tipo === 'branch') {
            this.$swal({
              type: 'info',
              title: 'QR Code',
              text: 'Selecione uma branch primeiro para gerar o QR Code.',
              timer: 3000,
              showConfirmButton: false
            });
            return;
          }

          // Verificar se PWA tem configuração antes de tentar obter URL
          if (categoria === 'pwa') {
            // Verificar cache primeiro
            if (this.pwaConfigCache[clienteId] === undefined) {
              await this.verificarPWAKRAKEN(clienteId);
            }
            
            if (!this.pwaConfigCache[clienteId]) {
              this.$swal({
                type: 'warning',
                title: 'PWA Indisponível',
                text: 'Este cliente não possui configuração PWAKRAKEN. O PWA não está disponível.',
                timer: 3000,
                showConfirmButton: false
              });
              return;
            }
          }

          const ambiente = tipo === 'prod' ? 'PRODUCAO' : tipo === 'local' ? 'LOCAL' : 'TESTE';
          
          // Mostrar modal de carregamento do SSO
          const tipoSSO = categoria === 'lp' ? 'lp' : categoria;
          this.$sso.showFeedbackModal(clienteId, ambiente, tipoSSO);
          
          try {
            // Obter URL via plugin SSO sem redirecionar
            console.log('[QRCODE] Obtendo URL SSO:', { categoria, clienteId, ambiente });
            url = await this.obterURLSSO(categoria, clienteId, ambiente);
            console.log('[QRCODE] URL obtida:', url);
            
            if (!url) {
              throw new Error('URL não foi retornada');
            }
            
            // Atualizar modal para sucesso
            this.$sso.updateFeedbackModal('success', clienteId, ambiente, tipoSSO);
            
            // Aguardar um pouco para mostrar sucesso
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Fechar modal de carregamento
            this.$sso.hideFeedbackModal();
            
            if (categoria === 'dashboard') {
              title = `${nomeCliente} - Dashboard ${ambiente}`;
            } else if (categoria === 'pwa') {
              title = `${nomeCliente} - PWA ${ambiente}`;
            } else if (categoria === 'lp') {
              title = `${nomeCliente} - Landing Page`;
            }
            
            // Mostrar modal de QR Code
            console.log('[QRCODE] Abrindo modal com:', { url, title });
            this.qrCodeUrl = url;
            this.qrCodeTitle = title;
            this.showQRCodeModal = true;
          } catch (error) {
            console.error('[QRCODE] Erro ao obter URL:', error);
            // Atualizar modal para erro
            this.$sso.updateFeedbackModal('error', clienteId, ambiente, tipoSSO, error.message || 'Erro ao obter URL');
            
            // Aguardar um pouco para mostrar erro
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Fechar modal de carregamento
            this.$sso.hideFeedbackModal();
            
            throw error;
          }
        } else {
          throw new Error('Categoria não suportada');
        }
      } catch (error) {
        console.error('Erro ao obter URL para QR Code:', error);
        this.$swal({
          type: 'error',
          title: 'Erro',
          text: error.message || 'Não foi possível gerar o QR Code.',
          timer: 3000,
          showConfirmButton: false
        });
      }
    },
    async obterURLSSO(categoria, clienteId, ambiente) {
      try {
        console.log('[obterURLSSO] Iniciando:', { categoria, clienteId, ambiente });
        if (categoria === 'dashboard') {
          // Obter token SSO e construir URL do dashboard
          const response = await this.$store.dispatch('autenticacao/initPolgoApiLoginSSOSession', {
            ambiente: ambiente === 'LOCAL' ? 'TESTE' : ambiente,
            cliente: clienteId
          });

          console.log('[obterURLSSO] Resposta SSO Dashboard:', response);
          if (response.status === 200 || response.status === 201) {
            const tokenJWT = response.tokenJWT;
            if (!tokenJWT) {
              throw new Error('Token JWT não foi retornado');
            }
            // Construir URL do dashboard (mesma lógica do redirectToDashboard)
            let dashboardUrl;
            if (ambiente === 'PRODUCAO') {
              dashboardUrl = `https://dash.polgo.com.br/login?cliente=${clienteId}&token=${tokenJWT}`;
            } else if (ambiente === 'LOCAL') {
              dashboardUrl = `http://localhost:8080/login?cliente=${clienteId}&token=${tokenJWT}`;
            } else {
              dashboardUrl = `https://testedash.polgo.com.br/login?cliente=${clienteId}&token=${tokenJWT}`;
            }
            console.log('[obterURLSSO] URL Dashboard gerada:', dashboardUrl);
            return dashboardUrl;
          } else {
            throw new Error(`Erro ao obter token SSO: ${response.msg || 'Status ' + response.status}`);
          }
        } else if (categoria === 'pwa') {
          // Obter URL do PWA
          if (ambiente === 'LOCAL') {
            // Para local, buscar configuração PWAKRAKEN
            const configPWAKRAKEN = await this.$store.dispatch('configuracoes/fetchSettingByClienteENome', {
              cliente: clienteId,
              nome: 'PWAKRAKEN'
            });

            if (configPWAKRAKEN.status === 200 && configPWAKRAKEN.dados) {
              const configId = configPWAKRAKEN.dados._id || configPWAKRAKEN.dados.id || configPWAKRAKEN.id;
              if (!configId) {
                throw new Error('ID da configuração PWAKRAKEN não encontrado');
              }
              const localUrl = `http://localhost:8080/?configID=${configId}`;
              console.log('[obterURLSSO] URL PWA Local gerada:', localUrl);
              return localUrl;
            } else {
              throw new Error('Configuração PWAKRAKEN não encontrada para ambiente local');
            }
          } else {
            // Para produção/homologação, buscar configuração PWA (mesma lógica do loginPWA)
            console.log('[obterURLSSO] Buscando configuração PWA para:', { clienteId, ambiente });
            
            let pwaUrls;
            try {
              pwaUrls = await this.$sso.buscarConfiguracaoPWA(clienteId);
              console.log('[obterURLSSO] Configuração PWA obtida:', pwaUrls);
              console.log('[obterURLSSO] Chaves disponíveis:', Object.keys(pwaUrls));
            } catch (configError) {
              console.error('[obterURLSSO] Erro ao buscar configuração PWA:', configError);
              throw configError;
            }
            
            // Mapear ambiente para chave (mesma lógica do loginPWA)
            const ambienteKey = ambiente === 'PRODUCAO' ? 'prod' : 'test';
            const pwaUrl = pwaUrls[ambienteKey];
            console.log('[obterURLSSO] URL PWA selecionada (chave:', ambienteKey, '):', pwaUrl);
            console.log('[obterURLSSO] Objeto completo pwaUrls:', JSON.stringify(pwaUrls, null, 2));
            
            if (pwaUrl) {
              // A URL já vem formatada do buscarConfiguracaoPWA
              console.log('[obterURLSSO] URL PWA final:', pwaUrl);
              return pwaUrl;
            } else {
              // Tentar fallback para a outra chave se disponível
              const fallbackKey = ambienteKey === 'prod' ? 'test' : 'prod';
              const fallbackUrl = pwaUrls[fallbackKey];
              console.log('[obterURLSSO] Tentando fallback (chave:', fallbackKey, '):', fallbackUrl);
              
              if (fallbackUrl) {
                console.log('[obterURLSSO] Usando URL do fallback:', fallbackUrl);
                return fallbackUrl;
              }
              
              const ambienteLabel = ambiente === 'PRODUCAO' ? 'produção' : 'homologação';
              throw new Error(`URL do PWA não encontrada para o ambiente ${ambienteLabel}. URLs disponíveis: ${Object.keys(pwaUrls).join(', ')}`);
            }
          }
        } else if (categoria === 'lp') {
          // Para LP, buscar URL via plugin SSO
          console.log('[obterURLSSO] Buscando URL LP para:', clienteId);
          const lpUrl = await this.$sso.buscarConfiguracaoLP(clienteId);
          console.log('[obterURLSSO] URL LP obtida:', lpUrl);
          if (!lpUrl) {
            throw new Error('URL da Landing Page não foi retornada');
          }
          return lpUrl;
        }

        throw new Error(`Categoria não suportada: ${categoria}`);
      } catch (error) {
        console.error('[obterURLSSO] Erro ao obter URL SSO:', error);
        throw error;
      }
    },
  },
};
</script>

<style>
.quicksearch-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(20, 0, 40, 0.92);
  z-index: 9999 !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quicksearch-modal {
  background: transparent;
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin-top: 7vh;
  animation: fadeIn 0.2s;
}

.quicksearch-searchbar-row {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
}

.quicksearch-searchbar-group {
  display: flex;
  align-items: center;
  background: var(--qs-panel-bg);
  border-radius: 2.5rem;
  box-shadow: var(--shadow-qs-md);
  padding: 0.2rem 1.2rem;
  width: 100%;
  max-width: 520px;
  min-width: 220px;
}

.quicksearch-searchbar-icon {
  color: var(--color-nav-accent);
  font-size: 1.35rem;
  margin-right: 0.7rem;
}

.quicksearch-searchbar-input {
  flex: 1;
  height: 3.2rem;
  font-size: 1.25rem;
  border: none;
  outline: none;
  background: transparent;
  color: var(--qs-panel-fg);
  padding-left: 0.2rem;
  padding-right: 1rem;
}

.quicksearch-close {
  position: absolute;
  top: 24px;
  right: 32px;
  background: none;
  border: none;
  color: var(--lp-text-on-dark);
  font-size: 2rem;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.quicksearch-close:hover {
  opacity: 1;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

/* Grid responsivo simplificado */
.quicksearch-grid-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.quicksearch-apps-panel {
  background: var(--qs-panel-bg);
  color: var(--qs-panel-fg);
  border-radius: 12px;
  box-shadow: var(--shadow-qs-sm);
  padding: 16px 20px;
  margin-bottom: 12px;
}

.quicksearch-section-title {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-secondary);
  margin-bottom: 12px;
}

.quicksearch-apps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 12px;
  justify-items: center;
}

.quicksearch-app-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 96px;
  text-decoration: none;
  color: inherit;
  transition: transform 0.15s ease;
}

.quicksearch-app-tile:hover,
.quicksearch-app-tile:focus-visible {
  transform: translateY(-2px);
  outline: none;
}

.quicksearch-app-tile__label {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--text-primary);
  text-align: center;
  line-height: 1.25;
  word-break: break-word;
}

.quicksearch-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  background: var(--qs-panel-bg);
  color: var(--qs-panel-fg);
  border-radius: 12px;
  box-shadow: var(--shadow-qs-sm);
  padding: 20px;
  max-height: 65vh;
  overflow-y: auto;
}

@media (max-width: 767px) {
  .quicksearch-grid {
    padding: 15px;
  }

  .quicksearch-grid-container {
    padding: 0 10px;
  }
}

.cliente-grid-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 10px;
  background: var(--qs-elevated);
  border: 1px solid var(--qs-border);
  transition: all 0.2s ease;
  cursor: pointer;
}

.cliente-grid-item:hover {
  background: var(--qs-border);
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

/* Não aplicar hover do card quando mouse está sobre atalhos */
.cliente-grid-item:hover .atalho-item:hover,
.cliente-grid-item:hover .atalho-dropdown-btn:hover {
  background: unset !important;
  box-shadow: unset !important;
  transform: unset !important;
}

.cliente-grid-item:has(.atalho-item:hover),
.cliente-grid-item:has(.atalho-dropdown-btn:hover) {
  background: var(--qs-elevated) !important;
  box-shadow: none !important;
  transform: none !important;
}

.cliente-header {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: auto;
}

.status-badge.inativo {
  background: var(--qs-gradient-danger);
  color: white;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.status-badge i {
  font-size: 0.8rem;
}



.cliente-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.cliente-nome {
  font-weight: bold;
  color: var(--qs-panel-fg);
  font-size: 1.1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cliente-identificacao {
  font-size: 0.9rem;
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.atalhos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 8px;
}

@media (max-width: 767px) {
  .atalhos-grid {
    grid-template-columns: 1fr;
  }
}

.atalho-item {
  display: flex;
  justify-content: center;
  align-items: center;

  min-height: 44px;
}

.atalho-item .atalho-dropdown-btn {
  width: 100%;
  font-size: 0.85rem;
  padding: 0.25rem 0.6rem !important;
  height: auto;
  min-height: 32px;
  border-radius: 6px;
}

/* Estados simplificados */
.empty-state,
.loading-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 20px;
  color: var(--text-muted);
}

.empty-icon i {
  font-size: 3rem;
  opacity: 0.6;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 8px;
}

.empty-subtext {
  font-size: 1rem;
  opacity: 0.8;
}

.loading-state {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--qs-elevated);
  border-radius: 8px;
  border: 1px solid var(--qs-border);
}

.skeleton-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(
    90deg,
    var(--qs-border) 25%,
    var(--qs-shimmer-mid) 50%,
    var(--qs-border) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.skeleton-line {
  height: 12px;
  background: linear-gradient(
    90deg,
    var(--qs-border) 25%,
    var(--qs-shimmer-mid) 50%,
    var(--qs-border) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-line.short {
  width: 60%;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

.quicksearch-hint {
  font-size: 0.9rem;
  color: var(--text-muted);
  opacity: 0.8;
  margin-top: 1rem;
  text-align: center;
}

mark {
  background: var(--secondary) !important;
  color: white !important;
  padding: 0 1px !important;
  border-radius: 2px !important;
  font-weight: 600 !important;
  margin: 0 !important;
}

/* Barra de rolagem simplificada */
.quicksearch-grid::-webkit-scrollbar {
  width: 4px;
}

.quicksearch-grid::-webkit-scrollbar-track {
  background-color: rgba(168, 111, 226, 0.1);
}

.quicksearch-grid::-webkit-scrollbar-thumb {
  background: var(--qs-gradient-brand);
  border-radius: 2px;
}

.quicksearch-grid::-webkit-scrollbar-thumb:hover {
  background: var(--qs-gradient-brand-hover);
}
</style>